import { SocRegimenGroup, SocRegimenReference, Trial } from '../types';

/** Stable React key when the same regimen name appears under multiple NCCN statuses. */
export function socRegimenGroupKey(group: Pick<SocRegimenGroup, 'regimenName' | 'preferredStatus'>): string {
  return `${group.regimenName}\u0000${group.preferredStatus}`;
}

/** Client-side fallback when regimen_groups are missing from the API (legacy response or failed fetch). */
export function buildRegimenGroupsFromTrials(trials: Trial[]): SocRegimenGroup[] {
  const byKey = new Map<string, SocRegimenGroup>();

  for (const trial of trials) {
    const regimenName = trial.nccnRegimen?.trim() || trial.expDrug?.trim() || 'Unspecified regimen';
    const preferredStatus = trial.preferredStatus;
    const groupKey = `${regimenName}\u0000${preferredStatus}`;
    let group = byKey.get(groupKey);
    if (!group) {
      group = {
        regimenName,
        preferredStatus,
        nccnNotes: trial.nccnNotes || '',
        pmids: [],
        referenceCount: 0,
        references: [],
      };
      byKey.set(groupKey, group);
    }
    const pmid = trial.pmid?.trim();
    if (pmid && !group.pmids.includes(pmid)) {
      group.pmids.push(pmid);
    }
    const ref: SocRegimenReference = {
      mapIdentifier: trial.mapIdentifier || trial.pmid || trial.id,
      refNum: trial.refNum || null,
      refContents: trial.refContents || null,
      pmid: pmid || null,
      trials: [trial],
      trial,
    };
    group.references.push(ref);
    group.referenceCount = group.references.length;
  }

  const statusRank: Record<Trial['preferredStatus'], number> = {
    Preferred: 0,
    'Other Recommended': 1,
    'Useful in Certain Circumstances': 2,
    'N/A': 3,
  };

  return Array.from(byKey.values()).sort(
    (a, b) =>
      (statusRank[a.preferredStatus] ?? 99) - (statusRank[b.preferredStatus] ?? 99) ||
      a.regimenName.localeCompare(b.regimenName),
  );
}
